import React from 'react';
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md';
import produce from 'immer';

import Button from '../../../../../components/Button';
import Select from '../../../../../components/input/Select';
import InputForm from '../../../../../components/form/InputForm';
import InputPercentForm from '../../../../../components/form/InputPercentForm';
import InputNumber from '../../../../../components/input/InputNumber';
import SwitchForm from '../../../../../components/form/SwitchForm';
import Input from '../../../../../components/input/Input';
import Switch from '../../../../../components/input/Switch';
import Img from '../../../../../components/Img/Img';
import todayDeliveryBadgeIcon from '../../../../../static/images/common/today_delivery_badge_icon.png';
import {
  FULFILLMENT_PRODUCT_MANAGEMENT_TYPE_LIST,
  MAXIMUM_NUMBER_OF_PRODUCT_NUMBER_TEXT,
  MAXIMUM_NUMBER_OF_PRODUCT_OPTION_VALUE_TEXT,
  MAXIMUM_NUMBER_OF_PRODUCT_STYLE_CODE_TEXT,
  PRODUCT_MANAGEMENT_TYPE,
  PRODUCT_MAXIMUM_PRICE,
  PRODUCT_MAXIMUM_STOCK,
  PRODUCT_MINIMUM_STOCK,
  PRODUCT_OPTION_STATUS,
  PRODUCT_OPTION_STATUS_SELECT_LIST,
} from '../../../../../constants/service/app/productConstants';
import { REX_TEN_WON_UNIT } from '../../../../../lib/rex';
import { roundDown, setNumberWithComma } from '../../../../../lib/common';
import { formRequiredRules } from '../../../../../lib/formRules';
import { setFeeMaximumNumber } from '../../../../../lib/utils/commonUtils';

/**
 * react-table v6의 normalizeComponent는 Cell이 함수일 때
 * React.createElement(Cell, cellInfo)로 렌더링한다.
 * 매 렌더마다 새로운 함수 참조가 생성되면 React가 다른 컴포넌트 타입으로 인식하여
 * 언마운트/리마운트가 발생한다.
 * form 입력이 포함된 Cell은 모듈 레벨에서 안정적인 참조로 정의하여
 * 리렌더 시에도 동일 컴포넌트로 인식되게 한다.
 */

const DeleteCell = (row) => {
  const { isDisableCell, deleteOption } = row.column;
  return (
    <Button
      type="button"
      color="primary"
      disabled={isDisableCell(row)}
      onClick={() => deleteOption(row.original.id)}
      text="삭제"
    />
  );
};

const IdCell = (row) => {
  return row.original.isNew ? '-' : row.value || '-';
};

const NameCell = (row) => {
  const { form, isEditable, setOptionList } = row.column;
  return (
    <InputForm
      form={form}
      name={`name_${row.original.id}`}
      rules={[formRequiredRules(true, null, true)]}
      disabled={row.original.selected || !isEditable}
      initialValue={row.value}
      onChange={(value) =>
        setOptionList((prev) =>
          prev.map((item, i) => (i === row.index ? { ...item, name: value } : item)),
        )
      }
      maxLength={MAXIMUM_NUMBER_OF_PRODUCT_OPTION_VALUE_TEXT}
      size="large"
    />
  );
};

const PriceCell = (row) => {
  const { form, isEditable, setOptionList, salesPrice, minimumPrice } = row.column;
  return (
    <InputForm
      form={form}
      name={`price_${row.original.id}`}
      type="number"
      disabled={!isEditable}
      style={{ width: 'auto' }}
      rules={[
        { pattern: REX_TEN_WON_UNIT, message: '10원 단위로 입력해주세요.' },
        {
          validator: (rule, value, cb) => {
            if (!salesPrice) cb('판매가를 입력해주세요.');
            else if (value < minimumPrice - salesPrice) {
              cb(`최소 옵션가는 ${setNumberWithComma(roundDown(minimumPrice - salesPrice), '원')}`);
            } else cb();
          },
        },
      ]}
      max={PRODUCT_MAXIMUM_PRICE}
      step={10}
      initialValue={row.value}
      onChange={(value) =>
        setOptionList((prev) =>
          prev.map((item, i) => (i === row.index ? { ...item, price: value } : item)),
        )
      }
    />
  );
};

const StockCountCell = (row) => {
  const { isEditable, setOptionList } = row.column;
  return (
    <InputNumber
      disabled={!isEditable}
      style={{ width: 130 }}
      min={PRODUCT_MINIMUM_STOCK}
      max={PRODUCT_MAXIMUM_STOCK}
      value={row.value}
      onChange={(value) =>
        setOptionList((prev) =>
          prev.map((item, i) =>
            i === row.index
              ? {
                  ...item,
                  stockCount: value,
                  status: value === PRODUCT_MINIMUM_STOCK ? PRODUCT_OPTION_STATUS.SOLD_OUT : item.status,
                }
              : item,
          ),
        )
      }
    />
  );
};

const StatusCell = (row) => {
  const { isEditable, setOptionList } = row.column;
  return (
    <Select
      disabled={!isEditable}
      list={PRODUCT_OPTION_STATUS_SELECT_LIST}
      onChange={(value) =>
        setOptionList((prev) =>
          prev.map((item, i) => (i === row.index ? { ...item, status: value } : item)),
        )
      }
      defaultValue={row.value}
      value={row.value}
      isTable={true}
      size="large"
    />
  );
};

const StyleCodeCell = (row) => {
  const { isEditable, setOptionList } = row.column;
  return (
    <Input
      disabled={!isEditable}
      value={row.value}
      onChange={(value) =>
        setOptionList((prev) =>
          prev.map((item, i) => (i === row.index ? { ...item, styleCode: value } : item)),
        )
      }
      maxLength={MAXIMUM_NUMBER_OF_PRODUCT_STYLE_CODE_TEXT}
      size="large"
    />
  );
};

const NumberCell = (row) => {
  const { isEditable, setOptionList } = row.column;
  return (
    <Input
      disabled={!isEditable}
      value={row.value}
      onChange={(value) =>
        setOptionList((prev) =>
          prev.map((item, i) => (i === row.index ? { ...item, number: value } : item)),
        )
      }
      maxLength={MAXIMUM_NUMBER_OF_PRODUCT_NUMBER_TEXT}
      size="large"
    />
  );
};

const ProductManagementTypeCell = (row) => {
  const { isEditable, setProductOptionProductManagementType } = row.column;
  return (
    <Select
      value={row.value}
      onChange={(value) =>
        setProductOptionProductManagementType({
          index: row.index,
          value,
        })
      }
      list={row.original.productManagementTypeSelectList}
      disabled={!isEditable || !row.original.isNew}
      isTable={true}
    />
  );
};

const TodayDeliveryCell = (row) => {
  if (row.original.productManagementType !== PRODUCT_MANAGEMENT_TYPE.ENTER) {
    return <Img className="today-delivery-badge-icon" clickAble={false} src={todayDeliveryBadgeIcon} />;
  } else {
    return '-';
  }
};

const IsGiftCell = (row) => {
  const { isEditable, setIsGift, setOptionList } = row.column;
  return (
    <Switch
      checked={row.value}
      disabled={!isEditable}
      onChange={(isGift) => {
        setIsGift({
          isGift,
          setData: () =>
            setOptionList((prev) =>
              prev.map((item, i) => (i === row.index ? { ...item, isGift } : item)),
            ),
        });
      }}
      data-testid={`switch-${row.original.id}`}
    />
  );
};

const FeeToggleCell = (row) => {
  const { isEditable, isHideFeeInfo, toggleFeeInfo } = row.column;
  if (isHideFeeInfo) {
    return <MdKeyboardArrowRight onClick={isEditable ? () => toggleFeeInfo() : undefined} />;
  } else {
    return <MdKeyboardArrowLeft onClick={isEditable ? () => toggleFeeInfo() : undefined} />;
  }
};

const IsPgFeeCell = (row) => {
  const { isEditable, setOptionList } = row.column;
  return (
    <SwitchForm
      checked={row.original.feeInfo.isPgFee}
      disabled={row.original.productManagementType === PRODUCT_MANAGEMENT_TYPE.PURCHASE || !isEditable}
      onChange={(value) =>
        setOptionList((prev) => {
          return produce(prev, (draft) => {
            draft[row.index]['feeInfo']['isPgFee'] = value;
          });
        })
      }
    />
  );
};

const AmondzFeeRateCell = (row) => {
  const { form, isEditable, setOptionList } = row.column;
  return (
    <InputPercentForm
      form={form}
      disabled={!isEditable}
      rules={[formRequiredRules(true, undefined, false)]}
      max={setFeeMaximumNumber([row.original.feeInfo.marketingFeeRate, row.original.feeInfo.fulfillmentFeeRate])}
      initialValue={row.original.feeInfo.amondzFeeRate}
      onChange={(value) =>
        setOptionList((prev) => {
          return produce(prev, (draft) => {
            draft[row.index]['feeInfo']['amondzFeeRate'] = value;
          });
        })
      }
      name={`feeInfo_amondzFeeRate_${row.original.id}`}
    />
  );
};

const MarketingFeeRateCell = (row) => {
  const { form, isEditable, setOptionList } = row.column;
  return (
    <InputPercentForm
      form={form}
      disabled={!isEditable}
      rules={[formRequiredRules(true, undefined, false)]}
      max={setFeeMaximumNumber([row.original.feeInfo.amondzFeeRate, row.original.feeInfo.fulfillmentFeeRate])}
      initialValue={row.original.feeInfo.marketingFeeRate}
      onChange={(value) =>
        setOptionList((prev) => {
          return produce(prev, (draft) => {
            draft[row.index]['feeInfo']['marketingFeeRate'] = value;
          });
        })
      }
      name={`feeInfo_marketingFeeRate_${row.original.id}`}
    />
  );
};

const FulfillmentFeeRateCell = (row) => {
  const { form, isEditable, setOptionList } = row.column;
  return (
    <InputPercentForm
      form={form}
      disabled={!isEditable}
      rules={[formRequiredRules(true, undefined, false)]}
      max={setFeeMaximumNumber([row.original.feeInfo.amondzFeeRate, row.original.feeInfo.marketingFeeRate])}
      initialValue={row.original.feeInfo.fulfillmentFeeRate}
      onChange={(value) =>
        setOptionList((prev) => {
          return produce(prev, (draft) => {
            draft[row.index]['feeInfo']['fulfillmentFeeRate'] = value;
          });
        })
      }
      name={`feeInfo_fulfillmentFeeRate_${row.original.id}`}
    />
  );
};

/**
 *  옵션 리스트 컬럼
 */
const columns = (data) => {
  const {
    isEditable,
    form,
    setOptionList,
    deleteOption,
    salesPrice,
    optionKeyName,
    minimumPrice,
    isHideFeeInfo,
    setProductOptionProductManagementType,
    setIsGift,
    toggleFeeInfo,
  } = data;

  const isDisableCell = (row) => {
    // 새로운 행은 수정가능
    if (row.original.isNew) {
      return false;
    }

    return !isEditable || FULFILLMENT_PRODUCT_MANAGEMENT_TYPE_LIST.includes(row.original.productManagementType);
  };

  return [
    {
      Header: '삭제',
      width: 100,
      fixed: 'left',
      isDisableCell,
      deleteOption,
      Cell: DeleteCell,
    },
    {
      Header: '옵션 ID',
      accessor: 'id',
      width: 100,
      fixed: 'left',
      Cell: IdCell,
    },
    {
      Header: '옵션',
      width: 200,
      columns: [
        {
          Header: optionKeyName,
          accessor: 'name',
          width: 200,
          form,
          isEditable,
          setOptionList,
          Cell: NameCell,
        },
      ],
    },
    {
      accessor: 'price',
      Header: '옵션가',
      width: 150,
      form,
      isEditable,
      setOptionList,
      salesPrice,
      minimumPrice,
      Cell: PriceCell,
    },
    {
      accessor: 'stockCount',
      Header: '재고',
      width: 150,
      isEditable,
      setOptionList,
      Cell: StockCountCell,
    },
    {
      accessor: 'status',
      Header: '판매',
      isEditable,
      setOptionList,
      Cell: StatusCell,
    },
    {
      accessor: 'styleCode',
      Header: '바코드',
      width: 200,
      isEditable,
      setOptionList,
      Cell: StyleCodeCell,
    },
    {
      accessor: 'number',
      Header: '관리 번호',
      isEditable,
      setOptionList,
      Cell: NumberCell,
    },
    {
      accessor: 'productManagementType',
      Header: '상품관리타입',
      width: 150,
      isEditable,
      setProductOptionProductManagementType,
      Cell: ProductManagementTypeCell,
    },
    {
      accessor: 'isTodayDelivery',
      Header: '오늘출발 여부',
      width: 150,
      Cell: TodayDeliveryCell,
    },
    {
      accessor: 'isGift',
      Header: '선물 여부',
      isEditable,
      setIsGift,
      setOptionList,
      Cell: IsGiftCell,
    },
    {
      accessor: 'isShowFeeInfo',
      Header: '수수료 열람',
      className: 'toggle',
      isEditable,
      isHideFeeInfo,
      toggleFeeInfo,
      Cell: FeeToggleCell,
    },
    {
      accessor: 'isPgFee',
      Header: 'PG 수수료',
      headerClassName: isHideFeeInfo ? 'hide' : '',
      className: isHideFeeInfo ? 'hide' : '',
      isEditable,
      setOptionList,
      Cell: IsPgFeeCell,
    },
    {
      accessor: 'amondzFeeRate',
      Header: '아몬즈 수수료',
      headerClassName: isHideFeeInfo ? 'hide' : '',
      className: isHideFeeInfo ? 'hide' : '',
      form,
      isEditable,
      setOptionList,
      Cell: AmondzFeeRateCell,
    },
    {
      accessor: 'marketingFeeRate',
      Header: '마케팅 수수료',
      headerClassName: isHideFeeInfo ? 'hide' : '',
      className: isHideFeeInfo ? 'hide' : '',
      form,
      isEditable,
      setOptionList,
      Cell: MarketingFeeRateCell,
    },
    {
      accessor: 'fulfillmentFeeRate',
      Header: '풀필먼트 수수료',
      headerClassName: data.isHideFeeInfo ? 'hide' : '',
      className: data.isHideFeeInfo ? 'hide' : '',
      form,
      isEditable,
      setOptionList,
      Cell: FulfillmentFeeRateCell,
    },
  ];
};

export default columns;
